import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [role, setRole] = useState(localStorage.getItem('auth_role'));
  const [businessId, setBusinessId] = useState(localStorage.getItem('business_id'));

  // Initialize axios header from localStorage on app start (avoids race condition)
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const loginSuperAdmin = async (username, password) => {
    try {
      const response = await axios.post(`${API}/superadmin/login`, {
        username,
        password
      });
      
      const { access_token, role: userRole } = response.data;
      
      // Set axios header immediately (synchronously) to avoid race condition with useEffect
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setRole(userRole);
      setUser({ username, role: userRole });
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_role', userRole);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const loginBusinessAdmin = async (slug, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        slug,
        password
      });
      
      const { access_token, role: userRole, business_id } = response.data;
      
      // Set axios header immediately (synchronously) to avoid race condition with useEffect
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setRole(userRole);
      setBusinessId(business_id);
      setUser({ slug, role: userRole, business_id });
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_role', userRole);
      localStorage.setItem('business_id', business_id);
      
      return { success: true, business_id };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setRole(null);
    setBusinessId(null);
    setUser(null);
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_role');
    localStorage.removeItem('business_id');
    
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        role,
        businessId,
        loginSuperAdmin,
        loginBusinessAdmin,
        logout,
        isAuthenticated: !!token,
        isSuperAdmin: role === 'superadmin',
        isBusinessAdmin: role === 'admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
